import React, { useMemo } from "react";
import * as Icons from "lucide-react";

const NEON = "#00FF99";

// Backend emits PascalCase icon names mapped to lucide. Fall back to Sparkles.
const resolveIcon = (name) => {
  if (!name) return Icons.Sparkles;
  return Icons[name] || Icons.Sparkles;
};

export default function ActionCard({ card, onConfirm, onCancel, isExecuting, executed = false, executedAt = null }) {
  // Hook must run unconditionally — `card?.icon` is fine when card is null,
  // resolveIcon falls back to Sparkles.  Early-return comes after.
  const Icon = useMemo(() => resolveIcon(card?.icon), [card?.icon]);
  if (!card) return null;

  const containerStyle = {
    background:    "rgba(0,255,153,0.05)",
    border:        "1px solid rgba(0,255,153,0.2)",
    borderRadius:  12,
    padding:       16,
    margin:        "8px 0",
    maxWidth:      340,
    opacity:       executed ? 0.7 : 1,
    transition:    "opacity 0.2s ease",
  };
  const headerStyle = {
    display:       "flex",
    alignItems:    "center",
    gap:           8,
    paddingBottom: 12,
    borderBottom:  "1px solid rgba(255,255,255,0.06)",
  };
  const titleStyle = {
    color:         "#fff",
    fontSize:      14,
    fontWeight:    600,
  };
  const fieldRow = {
    display:        "flex",
    justifyContent: "space-between",
    padding:        "6px 0",
    borderBottom:   "1px solid rgba(255,255,255,0.03)",
    gap:            12,
  };
  const labelStyle = {
    color:    "rgba(255,255,255,0.5)",
    fontSize: 12,
    flex:     "0 0 auto",
  };
  const valueStyle = {
    color:      "#fff",
    fontSize:   13,
    fontWeight: 500,
    textAlign:  "right",
    wordBreak:  "break-word",
  };
  const buttonsRow = {
    display:   "flex",
    gap:       8,
    marginTop: 14,
  };
  const baseBtn = {
    border:        "1px solid",
    borderRadius:  8,
    padding:       "8px 16px",
    fontSize:      13,
    fontWeight:    500,
    cursor:        isExecuting ? "wait" : "pointer",
    flex:          1,
    fontFamily:    "inherit",
    transition:    "background 0.15s",
    opacity:       isExecuting ? 0.6 : 1,
    display:       "flex",
    alignItems:    "center",
    justifyContent: "center",
    gap:           6,
  };
  const confirmBtn = {
    ...baseBtn,
    background:   "rgba(0,255,153,0.15)",
    borderColor:  "rgba(0,255,153,0.4)",
    color:        NEON,
  };
  const cancelBtn = {
    ...baseBtn,
    background:   "rgba(255,255,255,0.04)",
    borderColor:  "rgba(255,255,255,0.1)",
    color:        "rgba(255,255,255,0.5)",
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <Icon size={16} color={NEON} />
        <span style={titleStyle}>{card.title}</span>
      </div>

      <div style={{ paddingTop: 6 }}>
        {(card.fields || []).map((f, i) => (
          <div key={i} style={fieldRow}>
            <span style={labelStyle}>{f.label}</span>
            <span style={valueStyle}>{f.value}</span>
          </div>
        ))}
      </div>

      {executed ? (
        <div style={{
          marginTop:   14,
          paddingTop:  10,
          borderTop:   "1px solid rgba(0,255,153,0.15)",
          display:     "flex",
          alignItems:  "center",
          justifyContent: "center",
          gap:         6,
          color:       NEON,
          fontSize:    12,
          fontWeight:  500,
        }}>
          <Icons.CheckCircle size={14} />
          <span>Submitted{executedAt ? ` at ${executedAt}` : ""}</span>
        </div>
      ) : (
        <div style={buttonsRow}>
          <button
            type="button"
            style={confirmBtn}
            disabled={isExecuting}
            onMouseEnter={(e) => !isExecuting && (e.currentTarget.style.background = "rgba(0,255,153,0.25)")}
            onMouseLeave={(e) => !isExecuting && (e.currentTarget.style.background = "rgba(0,255,153,0.15)")}
            onClick={onConfirm}
          >
            {isExecuting ? <Spinner /> : <Icons.Check size={14} />}
            {isExecuting ? "Working…" : (card.confirm_text || "Confirm")}
          </button>
          <button
            type="button"
            style={cancelBtn}
            disabled={isExecuting}
            onMouseEnter={(e) => !isExecuting && (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            onMouseLeave={(e) => !isExecuting && (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
            onClick={onCancel}
          >
            <Icons.X size={14} />
            {card.cancel_text || "Cancel"}
          </button>
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      style={{
        width:        12,
        height:       12,
        borderRadius: "50%",
        border:       "2px solid rgba(0,0,0,0.2)",
        borderTopColor: NEON,
        animation:    "kiraSpin 0.7s linear infinite",
        display:      "inline-block",
      }}
    >
      <style>{`@keyframes kiraSpin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}
